import { useId } from 'react';
import ReactModal from 'react-modal';
import { Link } from 'react-router-dom';
import { IoIosClose } from 'react-icons/io';

import Box from 'components/ui/box';
import Button from 'components/ui/button';
import { NotificationDetail, ctaFrom, interpolate } from 'pages/Notificacoes/notification-inbox';
import { pruneEmailChrome } from 'utils/pruneEmailChrome';
import { sanitizeHtml } from 'utils/sanitizeHtml';

interface NotificationDetailModalProps {
  detail: NotificationDetail | null;
  loading: boolean;
  onClose: () => void;
}

/**
 * O aviso aberto renderiza a peça do e-mail **podada dos blocos que só valem numa
 * caixa de entrada** (CENTRAL-04.2, emendada na Fase 5).
 *
 * Antes da Fase 5 a peça vinha inteira, e dentro do app ela mentia: dizia "não é
 * necessário respondê-lo" e "você recebeu este e-mail" para quem não recebeu
 * e-mail nenhum, e oferecia "copie e cole este link no navegador" a quem já está
 * no navegador. Quem decide o que sai é o template, pelo `data-inapp="hide"`.
 *
 * A ordem das quatro operações não é arbitrária:
 *
 * 1. **Interpolar** antes de sanear — sanear primeiro deixaria as variáveis
 *    entrarem no HTML já filtrado, sem passar pelo filtro. Desde a Fase 3 o corpo
 *    do comunicado vem de um editor, então isso deixou de ser teórico.
 * 2. **Sanear** antes de podar — a poda é cosmética e a barreira de segurança não
 *    deve depender dela. Se o marcador sumir do template, o pior caso é rodapé
 *    feio; nunca HTML não saneado.
 * 3. **Podar**, que encontra os nós por `data-inapp` — atributo que o DOMPurify
 *    preserva por padrão (provado em `sanitizeHtml.test.ts`).
 * 4. **Injetar**.
 *
 * O botão do e-mail é um dos blocos podados, e o CTA daqui de baixo o substitui:
 * `<a>` injetado por `dangerouslySetInnerHTML` nunca vira `<Link>`, então clicar
 * nele recarregaria a SPA inteira. O botão do modal navega por rota.
 *
 * Ao contrário do regulamento, que chega sem classes e precisa da tipografia do
 * `regulamento-content/styles.scss`, a peça de e-mail traz estilo 100% inline: o
 * contêiner só precisa dar rolagem própria e não atrapalhar.
 */
export function NotificationDetailModal({ detail, loading, onClose }: NotificationDetailModalProps) {
  const titleId = useId();

  const inapp = detail?.inapp;
  const cta = ctaFrom(inapp);

  /** Título: o do catálogo quando existe; senão o assunto do próprio e-mail. */
  let titulo = detail?.subject ?? 'Aviso';
  if (inapp && detail) titulo = interpolate(inapp.titulo, detail.vars);

  const peca = detail?.content;

  return (
    <ReactModal
      isOpen={loading || !!detail}
      onRequestClose={onClose}
      contentLabel="Detalhe da notificação"
      aria={{ labelledby: titleId }}
      portalClassName="relative z-50"
      overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-60 flex flex-col items-center w-screen h-screen p-6 pt-[8vh] overflow-y-auto"
      className="outline-none w-[95%] lg:w-auto lg:max-w-[700px]"
      shouldReturnFocusAfterClose={true}
    >
      <Box className="relative">
        <button
          type="button"
          className="absolute -right-4 -top-4 rounded-full bg-purple w-10 h-10 p-0 flex justify-center items-center hover:ring hover:ring-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
          onClick={onClose}
          aria-label="Fechar notificação"
        >
          <IoIosClose color="white" size={40} aria-hidden="true" />
        </button>

        <h2 id={titleId} className="text-yellow text-xl lg:text-2xl mb-4 pr-8">
          {titulo}
        </h2>

        {loading && (
          <div role="status" aria-live="polite" className="py-8 text-center">
            <span className="sr-only">Carregando notificação</span>
            <span aria-hidden="true">Carregando...</span>
          </div>
        )}

        {!loading && detail && (
          <>
            {peca && (
              <div className="max-h-[55vh] overflow-y-auto rounded-lg bg-white" tabIndex={0}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: pruneEmailChrome(sanitizeHtml(interpolate(peca, detail.vars))),
                  }}
                />
              </div>
            )}

            {/* Template ausente: o aviso não some, cai para o texto do catálogo. */}
            {!peca && inapp && <p className="font-thin">{interpolate(inapp.texto, detail.vars)}</p>}

            {cta && (
              <div className="flex justify-center mt-6">
                {cta.url.startsWith('/') && (
                  <Link to={cta.url} onClick={onClose}>
                    <Button color="clear" className="leading-none p-2 bg-yellow text-blue-dark">
                      {cta.label}
                    </Button>
                  </Link>
                )}
                {!cta.url.startsWith('/') && (
                  <a href={cta.url} target="_blank" rel="noopener noreferrer">
                    <Button color="clear" className="leading-none p-2 bg-yellow text-blue-dark">
                      {cta.label}
                    </Button>
                  </a>
                )}
              </div>
            )}
          </>
        )}
      </Box>
    </ReactModal>
  );
}
